import React from "react";
import { Link } from "react-router-dom";
import { Heart, Coffee, ArrowRight, CheckCircle, Cpu, GlobeHemisphereWest, Bell, Wrench } from "@phosphor-icons/react";

// PayPal email-based donation (no PayPal.Me handle needed).
// `business=` is the merchant email; the rest sets up a single-step donation flow.
const PAYPAL_EMAIL = "dahook52@yahoo.com";

function paypalUrl(amount) {
  const params = new URLSearchParams({
    cmd: "_donations",
    business: PAYPAL_EMAIL,
    item_name: "Okanogan Highlands Firewatch",
    currency_code: "USD",
    no_recurring: "0",
    return: "https://okanoganhighlandsfirewatch.app/donate?thanks=1",
    cancel_return: "https://okanoganhighlandsfirewatch.app/donate",
  });
  if (amount) params.set("amount", String(amount));
  return `https://www.paypal.com/donate?${params.toString()}`;
}

const PRESET_AMOUNTS = [5, 15, 30, 60];

const COSTS = [
  { icon: Cpu, label: "Server & database hosting", value: "~$40/mo" },
  { icon: GlobeHemisphereWest, label: "NASA FIRMS satellite quota", value: "Free up to 5K/day" },
  { icon: Bell, label: "Web push notifications", value: "Free (VAPID)" },
  { icon: Wrench, label: "Domain + email delivery (Resend)", value: "~$20/yr" },
];

const FEATURES = [
  "Live WildCAD-E dispatch incidents from 8 WA dispatch centers",
  "NIFC WFIGS active fire perimeters + incident locations",
  "NASA VIIRS satellite hotspot detection (first warning, before dispatch)",
  "NWS evacuation alerts overlaid on the map",
  "WSDOT road closures + mountain pass conditions",
  "ALERTWildfire camera deep-links",
  "Saved locations with proximity push notifications",
  "RSS / iCal feeds for emergency-manager workflows",
];

export default function DonatePage() {
  const params = new URLSearchParams(window.location.search);
  const thanks = params.get("thanks") === "1";

  return (
    <main className="min-h-screen bg-tac-bg text-tac-text" data-testid="donate-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {thanks && (
          <div
            data-testid="donate-thanks"
            className="mb-8 px-4 py-3 rounded-sm border border-tac-primary/40 bg-tac-primary/10 flex items-start gap-3"
          >
            <CheckCircle size={20} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-display font-bold text-tac-text">
                Thank you — that means a lot.
              </div>
              <div className="text-[15px] text-tac-dim mt-0.5">
                Your contribution keeps Firewatch live and ad-free for everyone in the valley.
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <header className="mb-12 border-l-4 border-tac-primary pl-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-tac-primary mb-2">
            Support Firewatch
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-4 leading-tight">
            Keep it live.
            <br />
            <span className="text-tac-primary">Keep it free.</span>
          </h1>
          <p className="text-[15px] sm:text-[19px] text-tac-dim leading-relaxed max-w-2xl">
            Firewatch is operated as a public service — no ads, no tracking, no paywall.
            If it has helped you sleep better during a fire season, or saved you a phone
            call to dispatch, please consider chipping in. Every dollar goes directly to
            hosting, NASA satellite quota, and keeping the lights on through the smoke.
          </p>
        </header>

        {/* PayPal donate */}
        <section className="mb-12 p-6 sm:p-8 bg-tac-surface/50 border border-tac-border rounded-sm">
          <div className="flex items-center gap-2 mb-1">
            <Heart size={16} weight="fill" className="text-tac-primary" />
            <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-tac-primary">
              One-time donation
            </span>
          </div>
          <h2 className="font-display font-black text-2xl sm:text-3xl mb-2">
            Donate via PayPal
          </h2>
          <p className="text-[15px] text-tac-dim mb-6">
            Secure checkout via PayPal — works with any PayPal account, credit card,
            or debit card. No PayPal account required.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
            {PRESET_AMOUNTS.map((amt) => (
              <a
                key={amt}
                data-testid={`donate-amount-${amt}`}
                href={paypalUrl(amt)}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-1.5 px-4 py-4 border border-tac-border bg-tac-bg hover:border-tac-primary hover:bg-tac-primary/5 text-tac-text rounded-sm transition-all"
              >
                <span className="font-display font-black text-2xl sm:text-3xl">${amt}</span>
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="text-tac-faint group-hover:text-tac-primary group-hover:translate-x-0.5 transition-all"
                />
              </a>
            ))}
          </div>

          <a
            data-testid="donate-amount-custom"
            href={paypalUrl(null)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider bg-tac-primary text-white hover:bg-tac-primary/90 rounded-sm transition-colors"
          >
            <Coffee size={14} weight="bold" />
            Donate a custom amount
            <ArrowRight size={12} weight="bold" />
          </a>

          <div className="text-[15px] font-mono-tac text-tac-faint mt-4">
            PayPal redirects to a secure checkout page. Your card details are never seen by Firewatch.
          </div>
        </section>

        {/* What your $ pays for */}
        <section className="mb-12">
          <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-3">
            Where the money goes
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {COSTS.map(({ icon: Icon, label, value }) => (
              <div
                key={label}
                className="flex items-center gap-3 px-4 py-3 border border-tac-border bg-tac-surface/30 rounded-sm"
              >
                <Icon size={18} weight="duotone" className="text-tac-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-tac-text">{label}</div>
                </div>
                <div className="font-mono-tac tabular text-[14px] text-tac-dim shrink-0">{value}</div>
              </div>
            ))}
          </div>
        </section>

        {/* What you've already got */}
        <section className="mb-12">
          <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-3">
            What Firewatch already gives you — for free
          </div>
          <ul className="space-y-1.5">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-tac-text">
                <CheckCircle size={14} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Footer note + other ways */}
        <section className="border-t border-tac-border pt-8 mt-12">
          <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-3">
            Other ways to help
          </div>
          <ul className="space-y-2 text-[15px] text-tac-dim">
            <li>
              ★ <span className="text-tac-text">Tell your neighbors</span> — share{" "}
              <a
                href="https://okanoganhighlandsfirewatch.app"
                className="text-tac-primary hover:underline"
              >
                okanoganhighlandsfirewatch.app
              </a>{" "}
              on the local Facebook group, post a flyer at the General Store, get the word out
              before the next fire season.
            </li>
            <li>
              ★ <span className="text-tac-text">Become a Verified Reporter</span>: if you're a
              ground-truth witness during an incident — firefighter, sheriff, or a resident with
              good eyes — sign up and ping the admin to verify your account. Verified updates
              show with a badge on incident pages.
            </li>
            <li>
              ★ <span className="text-tac-text">Subscribe to the iCal feed</span> in your work
              calendar app — keeps emergency-managers informed without a single page refresh.
            </li>
          </ul>

          <div className="mt-8 text-[14px] font-mono-tac text-tac-faint">
            Questions? Email{" "}
            <a href="mailto:dahook52@yahoo.com" className="text-tac-dim hover:text-tac-primary">
              dahook52@yahoo.com
            </a>{" "}
            · ←{" "}
            <Link to="/" className="text-tac-dim hover:text-tac-primary">
              back to the dashboard
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
